import { z } from 'zod';
import type { OrganizationType } from './auth';

/**
 * GET {pos}/theme is NOT a color contract. It serializes the FULL applied
 * tenant Theme model wrapped in a `theme` key:
 *   { "theme": { ...all theme columns... } }
 * (pos-api/app/Http/Controllers/Api/POS/ThemeController.php:14-19;
 *  pos-api/app/Services/ThemeManager.php:22-39).
 *
 * `color_config` exists in the tenant schema but is cast to array ONLY in the
 * salesgroundai Theme model; pos-api's Theme model has no cast for it
 * (pos-api/app/Models/Theme.php:38-45), so over this API it may arrive as
 * null, a JSON STRING, or (if the backend adds the cast later) an object.
 * The adapter in src/api/theme.ts absorbs all three.
 *
 * ThemeManager aborts 404 {"message":"Theme not found"} when no theme row is
 * applied — callers treat any theme failure as "no runtime theme" and fall
 * back to defaults downstream.
 */

export const wireRawThemeSchema = z.looseObject({
  id: z.number().optional(),
  name: z.string().nullish(),
  site_name: z.string().nullish(),
  currency: z.string().nullish(),
  /** null | JSON string | {primary, secondary, ...} — see module docblock. */
  color_config: z.unknown().optional(),
});
export type WireRawTheme = z.infer<typeof wireRawThemeSchema>;

/**
 * Accepts the verified wrapped envelope and, defensively, a bare theme object
 * (docs/05-api-contract.md §1 described the response as "raw Theme").
 */
export const wireThemeResponseSchema = z.union([
  z.looseObject({ theme: wireRawThemeSchema.nullable() }),
  wireRawThemeSchema,
]);

/**
 * Internal theme context produced by the D12 client adapter
 * (01-architecture.md §6). `primary_color` is a VALIDATED hex or null;
 * `organization_type` is null whenever the backend did not state it — the
 * adapter never guesses. Fallback color (#00B8DB) is applied downstream by
 * the ThemeStore, not here.
 */
export interface ConsoleThemeContext {
  organization_type: OrganizationType | null;
  primary_color: string | null;
}
